import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    CircularProgress,
    IconButton,
    Divider,
    MenuItem,
    Select,
    ListItemText,
    Tooltip
} from '@mui/material';
import {
    ArrowBack,
    Print,
    Download,
    Send,
    Edit,
    Cancel,
    Add,
    Delete,
    Save,
    Close,
    AttachMoney,
    Warning,
    AddCircleOutline,
    VerticalAlignTop,
    VerticalAlignBottom,
    RequestQuote,
    ArrowUpward,
    ArrowDownward
} from '@mui/icons-material';
import { Alert, AlertTitle, Checkbox, FormControlLabel } from '@mui/material';
import styled from '@emotion/styled';
import { getInvoice, sendInvoice, cancelInvoice, updateInvoice, createCreditMemo, updateInvoiceLineItems, recordClientPayment, getInvoiceOrphans, addInvoiceOrphans, exportInvoicesIif } from '../redux/ducks/invoices';
import { downloadInvoicePdf, downloadBlob } from '../utils/downloadUtils';
import { showAlert } from '../redux/slices/alerts';
import AppDatePicker from './AppDatePicker';
import { statusSeverity } from '../utils/statusColors';
import { formatDayLong, formatMonthKey, formatDate, toLocalDay } from '../utils/dateUtils';

const EditableCell = styled(TextField)(({ theme }) => ({
    '& .MuiInputBase-input': {
        padding: '6px 8px',
        fontSize: theme.typography.size.md
    }
}));

const LoadingWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
}));

const PageBox = styled(Box)(({ theme }) => ({
    width: '100%',
    minWidth: 0,
    overflowX: 'clip',
    padding: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
        padding: theme.spacing(3),
    },
}));

const HeaderRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(3),
    gap: theme.spacing(1),
}));

const TitleBlock = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: '1 1 auto',
    minWidth: 0,
}));

const InvoiceTitle = styled(Typography)({
    overflowWrap: 'break-word',
    minWidth: 0,
});

const CreditMemoChip = styled(Chip)(({ theme }) => ({
    marginLeft: theme.spacing(1),
}));

const ActionButtonsRow = styled(Box)(({ theme }) => ({
    marginLeft: 'auto',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
        marginLeft: 0,
        width: '100%',
        '& > *': {
            flex: '1 1 calc(50% - 4px)',
        },
    },
}));

const InvoiceCard = styled(Card)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const InvoiceMetaRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
}));

const MetaColumn = styled(Box)(({ theme }) => ({
    textAlign: 'right',
    [theme.breakpoints.down('sm')]: {
        textAlign: 'left',
    },
}));

const StatusBadge = styled(Chip)(({ theme }) => ({
    marginBottom: theme.spacing(1),
}));

const SectionDivider = styled(Divider)(({ theme }) => ({
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
}));

const EditActionsRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(1),
    gap: theme.spacing(1),
}));

const ActionCell = styled(TableCell)({
    width: 120,
});

const RowActions = styled(Box)({
    display: 'flex',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
});

const DateCell = styled(TableCell)({
    whiteSpace: 'nowrap',
    width: 1,
});

const DatePickerCell = styled(TableCell)({
    whiteSpace: 'nowrap',
    width: 160,
});

const BillingTypeSelect = styled(Select)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    minWidth: 100,
}));

const HoursInput = styled(EditableCell)({ width: 100 });
const RateInput = styled(EditableCell)({ width: 100 });
const AmountInput = styled(EditableCell)({ width: 120 });

const SectionBox = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(3),
}));

const PaymentsTableContainer = styled(TableContainer)(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const HelperText = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const IssuesAlert = styled(Alert)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const GatewaySyncRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
}));

const CoveredAmount = styled('span')(({ theme }) => ({
    borderBottom: `1px dotted ${theme.palette.text.disabled}`,
    color: theme.palette.text.secondary,
    cursor: 'help',
}));

const OrphanCard = styled(Card)(({ theme }) => ({
    marginTop: theme.spacing(3),
}));

const OrphanIntro = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const OrphanActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
}));

const OrphanOptions = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
}));

const OrphanOption = styled(Box)({
    flex: '1 1 260px',
    minWidth: 0,
});

const OrphanOptionHint = styled(Typography)(({ theme }) => ({
    display: 'block',
    marginLeft: theme.spacing(4),
}));

const InvoiceView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [creditDialogOpen, setCreditDialogOpen] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [editData, setEditData] = useState({});
    const [creditData, setCreditData] = useState({ amount: '', notes: '' });

    const [editingLineItems, setEditingLineItems] = useState(false);
    const [workingLineItems, setWorkingLineItems] = useState([]);
    const [savingLineItems, setSavingLineItems] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [exportingIif, setExportingIif] = useState(false);

    // Mark as Paid
    const [paidDialogOpen, setPaidDialogOpen] = useState(false);
    const [paidData, setPaidData] = useState({ amount: '', date: '', method: 'Bank Transfer', externalId: '', notes: '' });
    const [savingPaid, setSavingPaid] = useState(false);

    // Orphans review
    const [orphans, setOrphans] = useState({ timeRecords: [], charges: [] });
    const [selectedOrphanTime, setSelectedOrphanTime] = useState(new Set());
    const [selectedOrphanCharges, setSelectedOrphanCharges] = useState(new Set());
    const [addingOrphans, setAddingOrphans] = useState(false);
    const [orphanZeroRate, setOrphanZeroRate] = useState(false);
    const [orphanKeepBillingType, setOrphanKeepBillingType] = useState(false);
    const [markPaidOrphansOpen, setMarkPaidOrphansOpen] = useState(false);
    const [markingOrphansPaid, setMarkingOrphansPaid] = useState(false);

    useEffect(() => {
        loadInvoice();
        loadOrphans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const loadOrphans = () => {
        dispatch(getInvoiceOrphans(id, (data) => {
            if (data) {
                setOrphans({ timeRecords: data.timeRecords || [], charges: data.charges || [] });
            }
        }));
    };

    const loadInvoice = () => {
        setLoading(true);
        dispatch(getInvoice(id, (response) => {
            setLoading(false);
            if (response) {
                setInvoice(response);
                setEditData({
                    invoiceDate: formatDate(response.invoiceDate),
                    dueDate: formatDate(response.dueDate),
                    notes: response.notes || ''
                });
            }
        }));
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            await downloadInvoicePdf(dispatch, invoice._id, invoice.invoiceNumber);
        } catch (err) {
            dispatch(showAlert({ severity: 'error', message: err.message || 'Failed to download PDF' }));
        } finally {
            setDownloading(false);
        }
    };

    const handleExportIif = () => {
        setExportingIif(true);
        dispatch(exportInvoicesIif({ ids: [invoice._id] }, (data) => {
            setExportingIif(false);
            if (!data) return;
            downloadBlob(data, `quickbooks-${invoice.invoiceNumber}.iif`, 'application/octet-stream');
        }));
    };

    const handleSend = () => {
        dispatch(sendInvoice(id, (response) => {
            if (response) {
                loadInvoice();
            }
        }));
    };

    const handleCancel = () => {
        dispatch(cancelInvoice(id, (response) => {
            if (response) {
                setCancelDialogOpen(false);
                loadInvoice();
            }
        }));
    };

    const handleEdit = () => {
        dispatch(updateInvoice(id, editData, (response) => {
            if (response) {
                setEditDialogOpen(false);
                loadInvoice();
            }
        }));
    };

    const openPaidDialog = () => {
        const total = invoice?.amount || 0;
        const alreadyPaid = (invoice?.payments || []).reduce((sum, p) => sum + (p.amount || 0), 0);
        const balance = Math.max(0, total - alreadyPaid);
        setPaidData({
            amount: balance.toFixed(2),
            date: toLocalDay(),
            method: 'Bank Transfer',
            externalId: '',
            notes: ''
        });
        setPaidDialogOpen(true);
    };

    const handleMarkPaid = () => {
        if (!paidData.amount || !paidData.date || !paidData.method) {
            dispatch(showAlert({ severity: 'error', message: 'Amount, date, and method are required' }));
            return;
        }
        setSavingPaid(true);
        dispatch(recordClientPayment(id, {
            amount: parseFloat(paidData.amount) || 0,
            date: paidData.date,
            method: paidData.method,
            externalId: paidData.externalId || undefined,
            notes: paidData.notes || undefined
        }, (response) => {
            setSavingPaid(false);
            if (response) {
                setPaidDialogOpen(false);
                loadInvoice();
            }
        }));
    };

    const toggleOrphanTime = (recordId) => {
        setSelectedOrphanTime(prev => {
            const next = new Set(prev);
            if (next.has(recordId)) next.delete(recordId);
            else next.add(recordId);
            return next;
        });
    };

    const toggleOrphanCharge = (chargeId) => {
        setSelectedOrphanCharges(prev => {
            const next = new Set(prev);
            if (next.has(chargeId)) next.delete(chargeId);
            else next.add(chargeId);
            return next;
        });
    };

    const toggleAllOrphanTime = () => {
        setSelectedOrphanTime(prev => {
            const ids = orphans.timeRecords.map(tr => tr._id);
            const allSelected = ids.length > 0 && ids.every(recordId => prev.has(recordId));
            return allSelected ? new Set() : new Set(ids);
        });
    };

    const toggleAllOrphanCharges = () => {
        setSelectedOrphanCharges(prev => {
            const ids = orphans.charges.map(ch => ch._id);
            const allSelected = ids.length > 0 && ids.every(chargeId => prev.has(chargeId));
            return allSelected ? new Set() : new Set(ids);
        });
    };

    const applyOrphanResponse = (response) => {
        setSelectedOrphanTime(new Set());
        setSelectedOrphanCharges(new Set());
        if (response._id) {
            setInvoice(response);
        } else {
            loadInvoice();
        }
        loadOrphans();
    };

    const handleAddOrphans = () => {
        if (selectedOrphanTime.size === 0 && selectedOrphanCharges.size === 0) return;
        setAddingOrphans(true);
        dispatch(addInvoiceOrphans(id, {
            timeRecordIds: [...selectedOrphanTime],
            chargeIds: [...selectedOrphanCharges],
            action: 'add',
            zeroRate: orphanZeroRate,
            keepBillingType: orphanKeepBillingType
        }, (response) => {
            setAddingOrphans(false);
            if (response) {
                applyOrphanResponse(response);
            }
        }));
    };

    const handleMarkOrphansPaid = () => {
        if (selectedOrphanTime.size === 0 && selectedOrphanCharges.size === 0) return;
        setMarkingOrphansPaid(true);
        dispatch(addInvoiceOrphans(id, {
            timeRecordIds: [...selectedOrphanTime],
            chargeIds: [...selectedOrphanCharges],
            action: 'markPaid'
        }, (response) => {
            setMarkingOrphansPaid(false);
            setMarkPaidOrphansOpen(false);
            if (response) {
                applyOrphanResponse(response);
            }
        }));
    };

    const handleCreateCredit = () => {
        dispatch(createCreditMemo(id, creditData, (response) => {
            if (response && response.invoice) {
                setCreditDialogOpen(false);
                loadInvoice();
            }
        }));
    };

    const startEditingLineItems = () => {
        const items = (invoice.lineItems || []).map(item => ({
            description: item.description || '',
            date: item.date || defaultLineDate(),
            hours: item.hours || 0,
            rate: item.rate || 0,
            amount: item.amount || 0,
            timeRecords: item.timeRecords || [],
            billingType: item.billingType || 'Hourly',
            isPayment: item.isPayment || false,
            isRetainerBase: item.isRetainerBase || false,
            isRetainerOverage: item.isRetainerOverage || false,
            paymentId: item.paymentId || undefined,
            isCharge: item.billingType === 'Charge' || item.billingType === 'Credit'
        }));
        setWorkingLineItems(items);
        setEditingLineItems(true);
    };

    const cancelEditingLineItems = () => {
        setEditingLineItems(false);
        setWorkingLineItems([]);
    };

    const handleLineItemChange = (index, field, value) => {
        if (field === 'hours' && workingLineItems[index]?.isRetainerBase) return;
        const updated = [...workingLineItems];
        updated[index] = { ...updated[index], [field]: value };
        if (field === 'hours' || field === 'rate') {
            const hours = field === 'hours' ? parseFloat(value) || 0 : parseFloat(updated[index].hours) || 0;
            const rate = field === 'rate' ? parseFloat(value) || 0 : parseFloat(updated[index].rate) || 0;
            if (!updated[index].isCharge && !updated[index].isPayment) {
                updated[index].amount = hours * rate;
            }
        }
        setWorkingLineItems(updated);
    };

    // A hand-added line has no time record to take a date from, so it starts on the last day of
    // the period being billed — where the retainer and overage lines already sit.
    const defaultLineDate = () => formatDate(invoice?.billingPeriodEnd || invoice?.invoiceDate) || toLocalDay();

    const addLineItem = (position) => {
        const blank = {
            description: '',
            date: defaultLineDate(),
            hours: 0,
            rate: 0,
            amount: 0,
            timeRecords: [],
            billingType: 'Hourly',
            isPayment: false,
            isRetainerBase: false,
            isRetainerOverage: false,
            isCharge: false
        };
        setWorkingLineItems((prev) => (position === 'top' ? [blank, ...prev] : [...prev, blank]));
    };

    const removeLineItem = (index) => {
        setWorkingLineItems(workingLineItems.filter((_, i) => i !== index));
    };

    const moveLineItem = (index, offset) => {
        const target = index + offset;
        if (target < 0 || target >= workingLineItems.length) return;
        const updated = [...workingLineItems];
        [updated[index], updated[target]] = [updated[target], updated[index]];
        setWorkingLineItems(updated);
    };

    const saveLineItems = () => {
        setSavingLineItems(true);
        const payload = workingLineItems.map(item => ({
            description: item.description,
            date: item.date || undefined,
            hours: parseFloat(item.hours) || 0,
            rate: parseFloat(item.rate) || 0,
            amount: parseFloat(item.amount) || 0,
            timeRecords: item.timeRecords || [],
            billingType: item.billingType,
            isPayment: item.isPayment || false,
            isRetainerBase: item.isRetainerBase || false,
            isRetainerOverage: item.isRetainerOverage || false,
            paymentId: item.paymentId || undefined
        }));
        dispatch(updateInvoiceLineItems(id, payload, (response) => {
            setSavingLineItems(false);
            if (response) {
                setEditingLineItems(false);
                setWorkingLineItems([]);
                setInvoice(response);
            }
        }));
    };

    const formatBillingPeriod = (startDate, endDate) => {
        const day = formatDate(endDate || startDate);
        return day ? formatMonthKey(`${day.slice(0, 4)}${day.slice(5, 7)}`) : '';
    };

    const formatQuantity = (item) => {
        if (item.isCharge || item.isPayment || item.isRetainerBase) return '1';
        const quantity = Number(item.quantity);
        return Number.isFinite(quantity) ? `${quantity.toFixed(2)} hrs` : '-';
    };

    const getStatusColor = (status) => statusSeverity('invoice', status);

    if (loading) {
        return (
            <LoadingWrapper>
                <CircularProgress />
            </LoadingWrapper>
        );
    }

    if (!invoice) {
        return (
            <PageBox>
                <Typography variant="h6">Invoice not found</Typography>
            </PageBox>
        );
    }

    let lineItems = [];

    if (invoice.lineItems && invoice.lineItems.length > 0) {
        lineItems = invoice.lineItems.map(item => ({
            description: item.description,
            date: item.date,
            quantity: item.hours,
            rate: item.rate || 0,
            amount: item.amount || 0,
            billingType: item.billingType,
            isRetainerBase: item.isRetainerBase,
            isRetainerOverage: item.isRetainerOverage,
            isPayment: item.isPayment,
            isCharge: item.billingType === 'Charge' || item.billingType === 'Credit',
            isCoveredByRetainer: item.billingType === 'Retainer' && !item.isRetainerBase && !item.isRetainerOverage && (item.amount || 0) === 0
        }));
    } else {
        if (invoice.timeRecords && invoice.timeRecords.length > 0) {
            const grouped = {};
            invoice.timeRecords.forEach(tr => {
                const key = `${tr.parentType}-${tr.parentId?._id || tr.parentId}`;
                if (!grouped[key]) {
                    grouped[key] = {
                        description: tr.parentId?.name || 'Unknown',
                        quantity: 0,
                        rate: 0,
                        amount: 0,
                        timeRecords: []
                    };
                }
                const hours = (tr.time || 0) / 60;
                grouped[key].quantity += hours;
                grouped[key].timeRecords.push(tr);
            });
            lineItems.push(...Object.values(grouped));
        }

        if (invoice.staticCharges && invoice.staticCharges.length > 0) {
            invoice.staticCharges.forEach(charge => {
                lineItems.push({
                    description: charge.name || charge.description,
                    date: charge.date,
                    quantity: 1,
                    rate: charge.amount || 0,
                    amount: charge.amount || 0,
                    isCharge: true
                });
            });
        }
    }

    const gatewayLabel = invoice.intuitId ? 'Intuit' : invoice.hubspotId ? 'HubSpot' : null;
    const gatewayPaymentUrl = invoice.intuitPaymentUrl || invoice.hubspotPaymentUrl || null;
    const isGatewaySynced = Boolean(gatewayLabel);
    const canEdit = (invoice.status === 'Draft' || invoice.status === 'Overdue') && !isGatewaySynced;
    const canSend = invoice.status === 'Draft';
    const canCancel = invoice.status !== 'Cancelled' && invoice.status !== 'Paid';
    const canMarkPaid = invoice.status === 'Open' || invoice.status === 'Overdue';
    const canAddOrphans = canEdit;
    // Marking orphans paid never touches the invoice totals, so it is allowed wherever the API allows it.
    const canMarkOrphansPaid = invoice.status !== 'Cancelled';
    const canSelectOrphans = canAddOrphans || canMarkOrphansPaid;

    const orphanTimeIds = orphans.timeRecords.map(tr => tr._id);
    const orphanChargeIds = orphans.charges.map(ch => ch._id);
    const selectedOrphanTimeCount = orphanTimeIds.filter(recordId => selectedOrphanTime.has(recordId)).length;
    const selectedOrphanChargeCount = orphanChargeIds.filter(chargeId => selectedOrphanCharges.has(chargeId)).length;
    const allOrphanTimeSelected = orphanTimeIds.length > 0 && selectedOrphanTimeCount === orphanTimeIds.length;
    const someOrphanTimeSelected = selectedOrphanTimeCount > 0 && !allOrphanTimeSelected;
    const allOrphanChargesSelected = orphanChargeIds.length > 0 && selectedOrphanChargeCount === orphanChargeIds.length;
    const someOrphanChargesSelected = selectedOrphanChargeCount > 0 && !allOrphanChargesSelected;
    const selectedOrphanCount = selectedOrphanTimeCount + selectedOrphanChargeCount;

    // Detect issues for the review banner
    const issueList = [];
    if (invoice.hasOrphans) {
        issueList.push('This invoice was generated with orphan records (Internal/Fixed Price/unmatched-Retainer time). Review the orphans below.');
    }
    const hasRetainerLines = (invoice.lineItems || []).some(li => li.billingType === 'Retainer');
    const hasRetainerBaseLine = (invoice.lineItems || []).some(li => li.isRetainerBase);
    if (hasRetainerLines && !hasRetainerBaseLine && (orphans.timeRecords.length === 0)) {
        issueList.push('Retainer line items exist but no Retainer Base line — verify pool allocation.');
    }
    const hasOverageLines = (invoice.lineItems || []).some(li => li.isRetainerOverage);
    if (hasOverageLines) {
        issueList.push('This invoice includes retainer overage charges.');
    }
    if (invoice.hasUnratedOverage) {
        issueList.push('Over-pool retainer hours could not be rated — the work has no skill, or that skill has no retainer rate for this client. Those hours were not billed.');
    }
    if (orphans.timeRecords.length > 0 || orphans.charges.length > 0) {
        issueList.push(`${orphans.timeRecords.length} orphan time record${orphans.timeRecords.length === 1 ? '' : 's'} and ${orphans.charges.length} orphan charge${orphans.charges.length === 1 ? '' : 's'} found in this invoice's scope.`);
    }
    const totalPayments = invoice.payments ? invoice.payments.reduce((sum, payment) => sum + (payment.amount || 0), 0) : 0;
    const remainingBalance = invoice.amount - totalPayments;

    const editingSubtotal = workingLineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

    const billingTypeOptions = ['Hourly', 'Retainer', 'Fixed Price', 'Charge', 'Credit'];

    return (
        <>
            <style>{`
                @media print {
                    @page {
                        margin: 0.5in;
                    }
                    body * {
                        visibility: hidden;
                    }
                    .invoice-print-content,
                    .invoice-print-content * {
                        visibility: visible;
                    }
                    .invoice-print-content {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                    }
                    .no-print {
                        display: none !important;
                    }
                }
            `}</style>
            <PageBox>
                <HeaderRow className="no-print">
                    <TitleBlock>
                        <IconButton onClick={() => navigate(-1)} aria-label="Back">
                            <ArrowBack />
                        </IconButton>
                        <InvoiceTitle variant="h4">
                            Invoice #{invoice.invoiceNumber}
                            {invoice.isCredit && <CreditMemoChip label="Credit Memo" size="small" color="secondary" />}
                        </InvoiceTitle>
                    </TitleBlock>
                    <ActionButtonsRow>
                        <Button startIcon={<Print />} onClick={() => window.print()}>
                            Print
                        </Button>
                        <Button startIcon={downloading ? <CircularProgress size={18} /> : <Download />} onClick={handleDownload} disabled={downloading}>
                            {downloading ? 'Downloading...' : 'Download PDF'}
                        </Button>
                        <Tooltip title={invoice.isCredit
                            ? 'Credit memos have to be entered in QuickBooks by hand — the IIF format has no encoding for them.'
                            : 'Downloads just this invoice as a QuickBooks .iif file, ready to import on its own.'}>
                            <span>
                                <Button
                                    startIcon={exportingIif ? <CircularProgress size={18} /> : <RequestQuote />}
                                    onClick={handleExportIif}
                                    disabled={exportingIif || invoice.isCredit}
                                >
                                    {exportingIif ? 'Exporting...' : 'QuickBooks IIF'}
                                </Button>
                            </span>
                        </Tooltip>
                        {canSend && (
                            <Button startIcon={<Send />} variant="contained" onClick={handleSend}>
                                Send
                            </Button>
                        )}
                        {canMarkPaid && (
                            <Button startIcon={<AttachMoney />} variant="contained" color="success" onClick={openPaidDialog}>
                                Mark Paid
                            </Button>
                        )}
                        {canEdit && (
                            <Button startIcon={<Edit />} onClick={() => setEditDialogOpen(true)}>
                                Edit
                            </Button>
                        )}
                        {canCancel && (
                            <Button startIcon={<Cancel />} color="error" onClick={() => setCancelDialogOpen(true)}>
                                Cancel Invoice
                            </Button>
                        )}
                        {!invoice.isCredit && (
                            <Button startIcon={<Add />} onClick={() => setCreditDialogOpen(true)}>
                                Create Credit
                            </Button>
                        )}
                    </ActionButtonsRow>
                </HeaderRow>

                {issueList.length > 0 && (
                    <IssuesAlert
                        severity={invoice.hasOrphans || (orphans.timeRecords.length + orphans.charges.length > 0) ? 'warning' : 'info'}
                        icon={<Warning />}
                        className="no-print"
                    >
                        <AlertTitle>Invoice issues to review</AlertTitle>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {issueList.map((msg, idx) => <li key={idx}>{msg}</li>)}
                        </ul>
                    </IssuesAlert>
                )}

                <InvoiceCard className="invoice-print-content">
                <CardContent>
                    <InvoiceMetaRow>
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                {invoice.client?.name || 'Unknown Client'}
                                {(invoice.billingPeriodStart || invoice.billingPeriodEnd) && ` - ${formatBillingPeriod(invoice.billingPeriodStart, invoice.billingPeriodEnd)}`}
                            </Typography>
                            {invoice.client?.address && (
                                <Typography variant="body2" color="text.secondary">
                                    {invoice.client.address.street && `${invoice.client.address.street}, `}
                                    {invoice.client.address.city && `${invoice.client.address.city}, `}
                                    {invoice.client.address.state && `${invoice.client.address.state} `}
                                    {invoice.client.address.postalCode}
                                </Typography>
                            )}
                            {invoice.client?.email && (
                                <Typography variant="body2" color="text.secondary">
                                    {invoice.client.email}
                                </Typography>
                            )}
                        </Box>
                        <MetaColumn>
                            <StatusBadge label={invoice.status} color={getStatusColor(invoice.status)} />
                            <Typography variant="body2" color="text.secondary">
                                Invoice Date: {formatDayLong(invoice.invoiceDate)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Due Date: {formatDayLong(invoice.dueDate)}
                            </Typography>
                            {invoice.sentDate && (
                                <Typography variant="body2" color="text.secondary">
                                    Sent: {formatDayLong(invoice.sentDate)}
                                </Typography>
                            )}
                            {invoice.paidDate && (
                                <Typography variant="body2" color="text.secondary">
                                    Paid: {formatDayLong(invoice.paidDate)}
                                </Typography>
                            )}
                            {isGatewaySynced && (
                                <GatewaySyncRow className="no-print">
                                    <Chip size="small" color="success" variant="outlined" label={`Synced to ${gatewayLabel}`} />
                                    {gatewayPaymentUrl && (
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            href={gatewayPaymentUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Pay Now
                                        </Button>
                                    )}
                                </GatewaySyncRow>
                            )}
                            {invoice.creditFor && (
                                <Typography variant="body2" color="text.secondary">
                                    Credit for: <Button size="small" onClick={() => navigate(`/billing/invoices/${invoice.creditFor._id}`)}>
                                        {invoice.creditFor.invoiceNumber}
                                    </Button>
                                </Typography>
                            )}
                        </MetaColumn>
                    </InvoiceMetaRow>

                    <SectionDivider />

                    {canEdit && !editingLineItems && (
                        <EditActionsRow className="no-print">
                            <Button size="small" startIcon={<Edit />} onClick={startEditingLineItems}>
                                Edit Line Items
                            </Button>
                        </EditActionsRow>
                    )}

                    {editingLineItems && (
                        <EditActionsRow className="no-print">
                            <Button size="small" startIcon={<VerticalAlignTop />} onClick={() => addLineItem('top')}>
                                Add Line to Top
                            </Button>
                            <Button size="small" startIcon={<VerticalAlignBottom />} onClick={() => addLineItem('bottom')}>
                                Add Line to Bottom
                            </Button>
                            <Button size="small" startIcon={<Close />} onClick={cancelEditingLineItems}>
                                Discard
                            </Button>
                            <Button size="small" startIcon={<Save />} variant="contained" onClick={saveLineItems} disabled={savingLineItems}>
                                {savingLineItems ? 'Saving...' : 'Save Line Items'}
                            </Button>
                        </EditActionsRow>
                    )}

                    <TableContainer component={Paper} variant="outlined">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <DateCell>
                                        <Tooltip title="Date of the work on this line. Retainer and overage lines cover a whole month, so they show its last day.">
                                            <span>Date</span>
                                        </Tooltip>
                                    </DateCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell align="right">Quantity/Hours</TableCell>
                                    <TableCell align="right">Rate</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                    {editingLineItems && <ActionCell align="center" className="no-print" />}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {editingLineItems ? (
                                    <>
                                        {workingLineItems.map((item, index) => (
                                            <TableRow key={index}>
                                                <DatePickerCell>
                                                    <AppDatePicker
                                                        size="small"
                                                        value={formatDate(item.date)}
                                                        onChange={(value) => value && handleLineItemChange(index, 'date', value)}
                                                    />
                                                </DatePickerCell>
                                                <TableCell>
                                                    <EditableCell
                                                        fullWidth
                                                        size="small"
                                                        value={item.description}
                                                        onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <BillingTypeSelect
                                                        size="small"
                                                        value={item.billingType}
                                                        onChange={(e) => handleLineItemChange(index, 'billingType', e.target.value)}
                                                    >
                                                        {billingTypeOptions.map(opt => (
                                                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                                        ))}
                                                    </BillingTypeSelect>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title={item.isRetainerBase ? "Retainer base lines are a flat fee — quantity is fixed at 1." : ''} disableHoverListener={!item.isRetainerBase}>
                                                        <span>
                                                            <HoursInput
                                                                size="small"
                                                                type="number"
                                                                value={item.hours}
                                                                onChange={(e) => handleLineItemChange(index, 'hours', e.target.value)}
                                                                inputProps={{ step: 0.25, min: 0, style: { textAlign: 'right' } }}
                                                                disabled={item.isRetainerBase}
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <RateInput
                                                        size="small"
                                                        type="number"
                                                        value={item.rate}
                                                        onChange={(e) => handleLineItemChange(index, 'rate', e.target.value)}
                                                        inputProps={{ step: 0.01, min: 0, style: { textAlign: 'right' } }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <AmountInput
                                                        size="small"
                                                        type="number"
                                                        value={item.amount}
                                                        onChange={(e) => handleLineItemChange(index, 'amount', e.target.value)}
                                                        inputProps={{ step: 0.01, style: { textAlign: 'right' } }}
                                                    />
                                                </TableCell>
                                                <TableCell align="center" className="no-print">
                                                    <RowActions>
                                                        <Tooltip title="Move line up">
                                                            <span>
                                                                <IconButton size="small" onClick={() => moveLineItem(index, -1)} disabled={index === 0}>
                                                                    <ArrowUpward fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <Tooltip title="Move line down">
                                                            <span>
                                                                <IconButton size="small" onClick={() => moveLineItem(index, 1)} disabled={index === workingLineItems.length - 1}>
                                                                    <ArrowDownward fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <IconButton size="small" color="error" onClick={() => removeLineItem(index)}>
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </RowActions>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell colSpan={5} align="right">
                                                <Typography variant="body1" fontWeight="bold">
                                                    New Subtotal:
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body1" fontWeight="bold">
                                                    ${editingSubtotal.toFixed(2)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </>
                                ) : (
                                    <>
                                        {lineItems.map((item, index) => (
                                            <TableRow key={index} hover>
                                                <DateCell>{formatDayLong(item.date)}</DateCell>
                                                <TableCell>
                                                    {item.description}
                                                </TableCell>
                                                <TableCell>
                                                    {item.billingType || 'Hourly'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatQuantity(item)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    ${(item.rate || 0).toFixed(2)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {item.isCoveredByRetainer ? (
                                                        <Tooltip title="Covered by the retainer pool, so nothing extra is billed for these hours. The pool's base charge may sit on another billing entity's invoice.">
                                                            <CoveredAmount>$0.00</CoveredAmount>
                                                        </Tooltip>
                                                    ) : (
                                                        `$${(item.amount || 0).toFixed(2)}`
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell colSpan={5} align="right">
                                                <Typography variant="body1" fontWeight="bold">
                                                    Subtotal:
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body1" fontWeight="bold">
                                                    ${(invoice.subtotal || 0).toFixed(2)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                        {invoice.taxAmount > 0 && (
                                            <TableRow>
                                                <TableCell colSpan={5} align="right">
                                                    <Typography variant="body1">
                                                        Tax ({invoice.taxRate}%):
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="body1">
                                                        ${(invoice.taxAmount || 0).toFixed(2)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        <TableRow>
                                            <TableCell colSpan={5} align="right">
                                                <Typography variant="h6" fontWeight="bold">
                                                    Total:
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="h6" fontWeight="bold">
                                                    ${(invoice.amount || 0).toFixed(2)} {invoice.currency || 'USD'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {invoice.payments && invoice.payments.length > 0 && (
                        <SectionBox>
                            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                Payment History
                            </Typography>
                            <PaymentsTableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Method</TableCell>
                                            <TableCell>Amount</TableCell>
                                            {invoice.payments.some(p => p.externalId) && (
                                                <TableCell>External ID</TableCell>
                                            )}
                                            {invoice.payments.some(p => p.notes) && (
                                                <TableCell>Notes</TableCell>
                                            )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {invoice.payments.map((payment, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{formatDayLong(payment.date)}</TableCell>
                                                <TableCell>{payment.method}</TableCell>
                                                <TableCell>${(payment.amount || 0).toFixed(2)}</TableCell>
                                                {invoice.payments.some(p => p.externalId) && (
                                                    <TableCell>{payment.externalId || '-'}</TableCell>
                                                )}
                                                {invoice.payments.some(p => p.notes) && (
                                                    <TableCell>{payment.notes || '-'}</TableCell>
                                                )}
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell colSpan={invoice.payments.some(p => p.externalId) ? 3 : 2} align="right">
                                                <Typography variant="body1" fontWeight="bold">
                                                    Total Paid:
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body1" fontWeight="bold">
                                                    ${totalPayments.toFixed(2)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                        {remainingBalance > 0 && (
                                            <TableRow>
                                                <TableCell colSpan={invoice.payments.some(p => p.externalId) ? 3 : 2} align="right">
                                                    <Typography variant="body1" fontWeight="bold">
                                                        Remaining Balance:
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body1" fontWeight="bold" color="error">
                                                        ${remainingBalance.toFixed(2)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </PaymentsTableContainer>
                        </SectionBox>
                    )}

                    {invoice.notes && (
                        <SectionBox>
                            <Typography variant="subtitle2" gutterBottom>
                                Notes:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {invoice.notes}
                            </Typography>
                        </SectionBox>
                    )}
                </CardContent>
            </InvoiceCard>

            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth className="no-print">
                <DialogTitle>Edit Invoice</DialogTitle>
                <DialogContent>
                    <Box mt={2} mb={1}>
                        <AppDatePicker
                            label="Invoice Date"
                            value={editData.invoiceDate}
                            onChange={(v) => setEditData({ ...editData, invoiceDate: v })}
                        />
                    </Box>
                    <Box mt={2} mb={1}>
                        <AppDatePicker
                            label="Due Date"
                            value={editData.dueDate}
                            onChange={(v) => setEditData({ ...editData, dueDate: v })}
                        />
                    </Box>
                    <TextField
                        fullWidth
                        label="Notes"
                        multiline
                        rows={4}
                        value={editData.notes}
                        onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleEdit} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>

            {/* Credit Memo Dialog */}
            <Dialog open={creditDialogOpen} onClose={() => setCreditDialogOpen(false)} maxWidth="sm" fullWidth className="no-print">
                <DialogTitle>Create Credit Memo</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Credit Amount"
                        type="number"
                        value={creditData.amount}
                        onChange={(e) => setCreditData({ ...creditData, amount: e.target.value })}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Description"
                        multiline
                        rows={4}
                        value={creditData.description}
                        onChange={(e) => setCreditData({ ...creditData, description: e.target.value })}
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreditDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreateCredit} variant="contained">Create Credit</Button>
                </DialogActions>
            </Dialog>

            {(orphans.timeRecords.length > 0 || orphans.charges.length > 0) && (
                <OrphanCard variant="outlined" className="no-print">
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Orphan records in this invoice's scope</Typography>
                        <OrphanIntro variant="body2" color="text.secondary">
                            Time records and charges that fell within this invoice's client / account / period
                            but were flagged as orphans during generation. Select rows and click <strong>Add to Invoice</strong>
                            to include them as line items, or <strong>Mark as Paid</strong> to settle them without billing them here.
                        </OrphanIntro>
                        {orphans.timeRecords.length > 0 && (
                            <SectionBox>
                                <Typography variant="subtitle2" gutterBottom>Time records ({orphans.timeRecords.length})</Typography>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell padding="checkbox">
                                                    <Tooltip title={allOrphanTimeSelected ? 'Clear every time record' : 'Select every time record'}>
                                                        <span>
                                                            <Checkbox
                                                                size="small"
                                                                checked={allOrphanTimeSelected}
                                                                indeterminate={someOrphanTimeSelected}
                                                                disabled={!canSelectOrphans}
                                                                onChange={toggleAllOrphanTime}
                                                                inputProps={{ 'aria-label': 'Select all orphan time records' }}
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell>Date</TableCell>
                                                <TableCell>User</TableCell>
                                                <TableCell>Description</TableCell>
                                                <TableCell align="right">Hours</TableCell>
                                                <TableCell>Type</TableCell>
                                                <TableCell>Reason</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {orphans.timeRecords.map(tr => {
                                                const summary = (tr.summary || '').trim();
                                                const parentName = (tr.parentId?.name || '').trim();
                                                const userLabel = tr.userName || [tr.user?.first_name, tr.user?.last_name].filter(Boolean).join(' ').trim() || tr.user?.email || '—';
                                                const billingTypeLabel = tr.resolvedBillingType || tr.billingType || '—';
                                                return (
                                                    <TableRow key={tr._id} hover>
                                                        <TableCell padding="checkbox">
                                                            <Checkbox
                                                                size="small"
                                                                checked={selectedOrphanTime.has(tr._id)}
                                                                disabled={!canSelectOrphans}
                                                                onChange={() => toggleOrphanTime(tr._id)}
                                                            />
                                                        </TableCell>
                                                        <TableCell>{formatDayLong(tr.date)}</TableCell>
                                                        <TableCell>{userLabel}</TableCell>
                                                        <TableCell>
                                                            <ListItemText
                                                                disableTypography
                                                                primary={
                                                                    <Typography variant="body2">
                                                                        {summary || parentName || 'Time entry'}
                                                                    </Typography>
                                                                }
                                                                secondary={
                                                                    summary && parentName
                                                                        ? <Typography variant="caption" color="text.secondary">on {parentName}</Typography>
                                                                        : null
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell align="right">{((tr.time || 0) / 60).toFixed(2)}</TableCell>
                                                        <TableCell>{billingTypeLabel}</TableCell>
                                                        <TableCell>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {tr.billingOrphanReason || '—'}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </SectionBox>
                        )}
                        {orphans.charges.length > 0 && (
                            <SectionBox>
                                <Typography variant="subtitle2" gutterBottom>Charges ({orphans.charges.length})</Typography>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell padding="checkbox">
                                                    <Tooltip title={allOrphanChargesSelected ? 'Clear every charge' : 'Select every charge'}>
                                                        <span>
                                                            <Checkbox
                                                                size="small"
                                                                checked={allOrphanChargesSelected}
                                                                indeterminate={someOrphanChargesSelected}
                                                                disabled={!canSelectOrphans}
                                                                onChange={toggleAllOrphanCharges}
                                                                inputProps={{ 'aria-label': 'Select all orphan charges' }}
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell>Date</TableCell>
                                                <TableCell>Name</TableCell>
                                                <TableCell>Description</TableCell>
                                                <TableCell align="right">Amount</TableCell>
                                                <TableCell>Reason</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {orphans.charges.map(ch => (
                                                <TableRow key={ch._id} hover>
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            size="small"
                                                            checked={selectedOrphanCharges.has(ch._id)}
                                                            disabled={!canSelectOrphans}
                                                            onChange={() => toggleOrphanCharge(ch._id)}
                                                        />
                                                    </TableCell>
                                                    <TableCell>{formatDayLong(ch.date)}</TableCell>
                                                    <TableCell>{ch.name}</TableCell>
                                                    <TableCell>{ch.description}</TableCell>
                                                    <TableCell align="right">${(ch.amount || 0).toFixed(2)}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {ch.billingOrphanReason || '—'}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </SectionBox>
                        )}
                        <SectionBox>
                            <Typography variant="subtitle2">Import options (apply to Add to Invoice)</Typography>
                            <OrphanOptions>
                                <OrphanOption>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={orphanZeroRate}
                                                disabled={!canAddOrphans}
                                                onChange={(e) => setOrphanZeroRate(e.target.checked)}
                                            />
                                        }
                                        label="Import at $0"
                                    />
                                    <OrphanOptionHint variant="caption" color="text.secondary">
                                        Brings the records onto the invoice with their hours visible, but nothing is billed for them.
                                    </OrphanOptionHint>
                                </OrphanOption>
                                <OrphanOption>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={orphanKeepBillingType}
                                                disabled={!canAddOrphans}
                                                onChange={(e) => setOrphanKeepBillingType(e.target.checked)}
                                            />
                                        }
                                        label="Keep original billing type"
                                    />
                                    <OrphanOptionHint variant="caption" color="text.secondary">
                                        Keeps each record's own billing type (Fixed Price, Retainer, Internal) instead of importing it as Hourly.
                                    </OrphanOptionHint>
                                </OrphanOption>
                            </OrphanOptions>
                        </SectionBox>
                        <OrphanActions>
                            <Button
                                variant="outlined"
                                color="warning"
                                startIcon={<AttachMoney />}
                                onClick={() => setMarkPaidOrphansOpen(true)}
                                disabled={!canMarkOrphansPaid || addingOrphans || markingOrphansPaid || selectedOrphanCount === 0}
                            >
                                Mark as Paid (don't invoice)
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={addingOrphans ? <CircularProgress size={16} /> : <AddCircleOutline />}
                                onClick={handleAddOrphans}
                                disabled={!canAddOrphans || addingOrphans || markingOrphansPaid || selectedOrphanCount === 0}
                            >
                                {canAddOrphans
                                    ? `Add ${selectedOrphanCount} to invoice`
                                    : 'Cannot add — invoice not editable'}
                            </Button>
                        </OrphanActions>
                    </CardContent>
                </OrphanCard>
            )}

            {/* Mark orphans as paid confirmation */}
            <Dialog open={markPaidOrphansOpen} onClose={() => setMarkPaidOrphansOpen(false)} maxWidth="sm" fullWidth className="no-print">
                <DialogTitle>Mark selected records as paid?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This marks {selectedOrphanTimeCount} time record{selectedOrphanTimeCount === 1 ? '' : 's'} and {selectedOrphanChargeCount} charge
                        {selectedOrphanChargeCount === 1 ? '' : 's'} as already paid. Nothing is added to invoice #{invoice.invoiceNumber} and its total
                        does not change. These records will stop showing up as orphans on this invoice and on every future one.
                    </DialogContentText>
                    <HelperText variant="body2" color="text.secondary">
                        This cannot be undone from this screen.
                    </HelperText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMarkPaidOrphansOpen(false)} disabled={markingOrphansPaid}>Keep as orphans</Button>
                    <Button onClick={handleMarkOrphansPaid} variant="contained" color="warning" disabled={markingOrphansPaid}>
                        {markingOrphansPaid ? <CircularProgress size={20} /> : 'Mark as Paid'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Mark as Paid Dialog */}
            <Dialog open={paidDialogOpen} onClose={() => setPaidDialogOpen(false)} maxWidth="sm" fullWidth className="no-print">
                <DialogTitle>Record Payment</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Amount"
                        type="number"
                        inputProps={{ step: '0.01', min: '0' }}
                        value={paidData.amount}
                        onChange={(e) => setPaidData({ ...paidData, amount: e.target.value })}
                        margin="normal"
                        required
                    />
                    <Box mt={2} mb={1}>
                        <AppDatePicker
                            label="Payment Date"
                            value={paidData.date}
                            onChange={(v) => setPaidData({ ...paidData, date: v })}
                            required
                        />
                    </Box>
                    <TextField
                        select
                        fullWidth
                        label="Method"
                        value={paidData.method}
                        onChange={(e) => setPaidData({ ...paidData, method: e.target.value })}
                        margin="normal"
                        required
                    >
                        <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                        <MenuItem value="ACH">ACH</MenuItem>
                        <MenuItem value="Wire">Wire</MenuItem>
                        <MenuItem value="Credit Card">Credit Card</MenuItem>
                        <MenuItem value="Check">Check</MenuItem>
                        <MenuItem value="Cash">Cash</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                    </TextField>
                    <TextField
                        fullWidth
                        label="Reference / External ID"
                        value={paidData.externalId}
                        onChange={(e) => setPaidData({ ...paidData, externalId: e.target.value })}
                        margin="normal"
                        helperText="Transaction ID, check number, or other reference"
                    />
                    <TextField
                        fullWidth
                        label="Notes"
                        multiline
                        rows={2}
                        value={paidData.notes}
                        onChange={(e) => setPaidData({ ...paidData, notes: e.target.value })}
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPaidDialogOpen(false)} disabled={savingPaid}>Cancel</Button>
                    <Button onClick={handleMarkPaid} variant="contained" color="success" disabled={savingPaid}>
                        {savingPaid ? <CircularProgress size={20} /> : 'Record Payment'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cancel Invoice Confirmation Dialog */}
            <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="sm" fullWidth className="no-print">
                <DialogTitle>Cancel Invoice</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to cancel invoice <strong>#{invoice.invoiceNumber}</strong>?
                    </Typography>
                    <HelperText variant="body2" color="text.secondary">
                        All associated time records will be reset to Pending status and can be re-invoiced. Static charges and fixed price payments will also be reset.
                    </HelperText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelDialogOpen(false)}>Keep Invoice</Button>
                    <Button onClick={handleCancel} variant="contained" color="error">Cancel Invoice</Button>
                </DialogActions>
            </Dialog>
            </PageBox>
        </>
    );
};

export default InvoiceView;
